
/** @jsxImportSource react */
import React, { useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from '../components/ui/Toast';
import { useAuth } from '../contexts/auth'
import { api } from '../services/api'
import { useTranslation } from '../i18n'
import { OrderType, UserRole } from '../types'
import {
  Package,
  Link as LinkIcon,
  Hash,
  FileText,
  Upload,
  Sparkles,
  MessageSquare,
  Truck,
  X,
  Loader2
} from 'lucide-react'
import { Button } from '../components/ui/Button';


interface NewOrderPageProps {
  onClose: () => void;
}

const NewOrderPage: React.FC<NewOrderPageProps> = ({ onClose }) => {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const { t } = useTranslation()
  // Form state
  const [aiDescription, setAiDescription] = React.useState('')
  const [productName, setProductName] = React.useState('')
  const [productLink, setProductLink] = React.useState('')
  const [quantity, setQuantity] = React.useState(1)
  const [notes, setNotes] = React.useState('')
  const [trackingNumber, setTrackingNumber] = React.useState('')
  const [image, setImage] = React.useState<File | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  // AI suggestions mutation
  const aiMutation = useMutation({
    mutationFn: (description: string) =>
      api.getSourcingSuggestions(description),
    onSuccess: (data) => {
      toast.success(t('aiSuggestionsGenerated'));
      setProductName(data.productName || '')
      setNotes(data.suggestedNotes || '')
    },
    onError: (error: Error) => {
      toast.error(
        t('aiAssistantError', {
          message: error.message,
        }),
      )
    },
  })
  
  // Order creation mutation
  const orderMutation = useMutation({
    mutationFn: api.createOrder,
    onSuccess: () => {
      toast.success(t('orderCreatedSuccess'));
      queryClient.invalidateQueries({
        queryKey: ['orders'],
      })
      onClose();
    },
    onError: () => {
      toast.error(t('createOrderError'))
      setIsSubmitting(false); // Reset loading state on error
    },
  })

  const handleGetAiSuggestions = () => {
    if (!aiDescription.trim()) {
      toast.error(t('aiDescriptionRequired'));
      return
    }
    aiMutation.mutate(aiDescription)
  }

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files && e.target.files[0]) {
          const file = e.target.files[0];
          // Client-side validation for file size (e.g., 10MB)
          if (file.size > 10 * 1024 * 1024) {
             toast.error(t('fileTooLargeError', { size: '10MB' }));
             return;
          }
          setImage(file);
      }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!user || user.role !== UserRole.Client) {
      toast.error('You must be a client to create an order.')
      return
    }
    
    // 1. Validation
    if (!productName.trim()) {
      toast.error(t('productNameRequired'));
      return
    }
    if (quantity < 1) {
        toast.error(t('quantityLabel') + ' must be at least 1');
        return;
    }

    setIsSubmitting(true);

    let imageUrl = '';
    
    // 2. Upload Image if exists
    if (image) {
      try {
        imageUrl = await api.uploadFile(image, 'order_images');
      } catch (err: any) {
        console.error("Upload failed", err);
        let errorMessage = t('uploadFailedError') || 'File upload failed. Please try again.';
        if (err.code === 'storage/unauthorized') {
            errorMessage = t('storageUnauthorizedError') || "Permission denied. You are not authorized to upload this file.";
        } else if (err.message && err.message.includes('Please wait')) {
            errorMessage = err.message;
        }
        toast.error(errorMessage);
        setIsSubmitting(false);
        return;
      }
    }
    
    // 3. Submit Order
    orderMutation.mutate({
      clientId: user.id,
      productName,
      productLink,
      quantity,
      notes,
      productImageUrl: imageUrl,
      orderType: OrderType.Sourcing,
      trackingNumber: trackingNumber || undefined,
    })
  }

  return (
    <div className="space-y-6">
      <p className="text-slate-600">
        {t('orderRequestDesc') ||
          "Fill out the details below to create a new sourcing order. We'll find the best suppliers for your requirements."}
      </p>
      {/* AI Sourcing Assistant Section */}
      <div className="p-6 rounded-lg bg-primary-50 border border-primary-200 shadow-sm">
        <div className="flex items-center mb-3">
          <Sparkles className="h-5 w-5 me-2 text-primary-600" />
          <h3 className="font-bold text-lg text-slate-800">
            {t('aiSourcingAssistant') || 'AI Sourcing Assistant'}
          </h3>
        </div>
        <p className="text-sm text-slate-600 mb-4">
          {t('aiSourcingAssistantDesc') ||
            "Describe what you're looking for and our AI will help find the right product specifications."}
        </p>
        <div className="relative">
           <div className={`absolute top-3 start-3 text-slate-400 pointer-events-none`}>
            <MessageSquare className="w-5 h-5" />
          </div>
          <textarea
            value={aiDescription}
            onChange={(e) => setAiDescription(e.target.value)}
            placeholder={t('aiDescriptionPlaceholder')}
            className={`w-full p-3 border border-slate-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent ps-10`}
            rows={4}
          />
        </div>
        <button
          type="button"
          onClick={handleGetAiSuggestions}
          disabled={aiMutation.isPending}
          className="mt-3 px-4 py-2.5 bg-gradient-to-r from-primary-600 to-primary-500 text-white rounded-lg hover:from-primary-700 hover:to-primary-600 transition-colors shadow-sm flex items-center"
        >
          {aiMutation.isPending ? (
            <>
              <Loader2 className="animate-spin -ms-1 me-2 h-4 w-4 text-white" />
              {t('generating') || 'Generating...'}
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4 me-2" />
              {t('getAiSuggestions') || 'Get AI Suggestions'}
            </>
          )}
        </button>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label
            htmlFor="productName"
            className="block text-gray-700 font-bold mb-2 flex items-center"
          >
            {t('productName') || 'Product Name'}
            <span className="text-red-500 ms-1">*</span>
          </label>
           <div className="relative">
                <div className={`absolute inset-y-0 start-3 flex items-center pointer-events-none`}>
                    <Package size={18} className="text-slate-400" />
                </div>
                <input
                    type="text"
                    id="productName"
                    value={productName}
                    onChange={(e) => setProductName(e.target.value)}
                    placeholder={t('productNamePlaceholder')}
                    required
                    className={`w-full p-3 border rounded-lg shadow-sm appearance-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent border-slate-300 ps-10`}
                />
            </div>
          <p className="text-xs text-slate-500 mt-1">
            {t('productNameHelp') ||
              'Provide a clear name or description of the product you want to source'}
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label
              htmlFor="productLink"
              className="block text-gray-700 font-bold mb-2 flex items-center"
            >
              {t('productLinkOptional') || 'Product Link (Optional)'}
            </label>
             <div className="relative">
                <div className={`absolute inset-y-0 start-3 flex items-center pointer-events-none`}>
                    <LinkIcon size={18} className="text-slate-400" />
                </div>
                <input
                    type="url"
                    id="productLink"
                    value={productLink}
                    onChange={(e) => setProductLink(e.target.value)}
                    placeholder={t('productLinkPlaceholder')}
                    className={`w-full p-3 border rounded-lg shadow-sm appearance-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent border-slate-300 ps-10`}
                />
            </div>
          </div>
          <div>
            <label
              htmlFor="trackingNumber"
              className="block text-gray-700 font-bold mb-2 flex items-center"
            >
              {t('trackingNumber')} ({t('optional')})
            </label>
            <div className="relative">
              <div className={`absolute inset-y-0 start-3 flex items-center pointer-events-none`}>
                <Truck size={18} className="text-slate-400" />
              </div>
              <input
                type="text"
                id="trackingNumber"
                value={trackingNumber}
                onChange={(e) => setTrackingNumber(e.target.value)}
                placeholder={t('trackingNumberPlaceholder')}
                className={`w-full p-3 border rounded-lg shadow-sm appearance-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent border-slate-300 ps-10`}
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label
              htmlFor="quantity"
              className="block text-gray-700 font-bold mb-2 flex items-center"
            >
              {t('quantityLabel') || 'Quantity'}
              <span className="text-red-500 ms-1">*</span>
            </label>
             <div className="relative">
                <div className={`absolute inset-y-0 start-3 flex items-center pointer-events-none`}>
                    <Hash size={18} className="text-slate-400" />
                </div>
                <input
                    type="number"
                    id="quantity"
                    value={quantity}
                    onChange={(e) =>
                        setQuantity(Math.max(1, parseInt(e.target.value) || 1))
                    }
                    min="1"
                    placeholder={t('quantityPlaceholder')}
                    required
                    className={`w-full p-3 border rounded-lg shadow-sm appearance-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent border-slate-300 ps-10`}
                />
            </div>
          </div>
        </div>

        <div>
          <label
            htmlFor="notes"
            className="block text-gray-700 font-bold mb-2 flex items-center"
          >
            {t('additionalSpecifications') || 'Additional Specifications'}
          </label>
           <div className="relative">
                <div className={`absolute top-3 start-3 flex items-center pointer-events-none`}>
                    <FileText size={18} className="text-slate-400" />
                </div>
                <textarea
                    id="notes"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder={t('notesPlaceholder')}
                    className={`w-full p-3 border rounded-lg shadow-sm appearance-none focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent border-slate-300 ps-10`}
                    rows={4}
                ></textarea>
            </div>
        </div>
        <div>
          <label
            htmlFor="image"
            className="block text-gray-700 font-bold mb-2 flex items-center"
          >
            <Upload className="h-4 w-4 me-2 text-primary-500" />
            {t('productImageOptional') || 'Product Image (Optional)'}
          </label>
          <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-dashed border-slate-300 rounded-lg">
            <div className="space-y-1 text-center">
              <svg
                className="mx-auto h-12 w-12 text-slate-400"
                stroke="currentColor"
                fill="none"
                viewBox="0 0 48 48"
                aria-hidden="true"
              >
                <path
                  d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H8m36-12h-4m4 0H20"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <div className="flex text-sm text-slate-600">
                <label
                  htmlFor="image"
                  className="relative cursor-pointer rounded-md font-medium text-primary-600 hover:text-primary-500"
                >
                  <span>{t('uploadAFile') || 'Upload a file'}</span>
                  <input
                    type="file"
                    id="image"
                    onChange={handleImageChange}
                    accept="image/*"
                    className="sr-only"
                  />
                </label>
                <p className="ps-1">
                  {t('orDragAndDrop') || 'or drag and drop'}
                </p>
              </div>
              <p className="text-xs text-slate-500">
                {t('imageTypes') || 'PNG, JPG, GIF up to 10MB'}
              </p>
              {image && (
                <p className="text-sm text-primary-600 font-medium flex items-center justify-center mt-2">
                  <span className="truncate max-w-xs">{image.name}</span>
                  <button
                    type="button"
                    onClick={() => setImage(null)}
                    className="ms-2 text-slate-400 hover:text-red-500"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="flex justify-between items-center pt-6 border-t border-slate-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-slate-300 text-slate-700 rounded-lg hover:bg-slate-50 transition-colors"
            disabled={isSubmitting}
          >
            {t('cancel') || 'Cancel'}
          </button>
          <Button
            type="submit"
            disabled={isSubmitting}
            isLoading={isSubmitting}
          >
             {t('submitOrder') || 'Submit Order'}
          </Button>
        </div>
      </form>
    </div>
  )
}
export default NewOrderPage;